import { FontColorEnum, FormField, FormHint } from '@acceso/design-system';
import { Controller, FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ScheduleItemType, TScheduleWithDeliveryItem } from '@/types/schedules';
import { getArrayOfDays } from '@/utils/date';

import { DailySchedulesWithDelivery } from '../DailySchedulesWithDelivery/DailySchedulesWithDelivery';
import { ScheduleWithDeliveryFormData } from '../DailySchedulesWithDelivery/types';

type TInputSchedulesProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  name: Path<T>;
  required?: boolean;
  disabled?: boolean;
  isOptionalEndHour?: boolean;
};

export function InputSchedulesWithDelivery<T extends FieldValues>({
  form,
  name,
  required,
  disabled,
  isOptionalEndHour,
}: TInputSchedulesProps<T>) {
  const { t } = useTranslation();
  const { control } = form;

  return (
    <FormField>
      <Controller
        disabled={disabled}
        name={name}
        control={control}
        rules={{
          required: required ? { value: true, message: t('El campo es obligatorio') } : false,
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          const formValue = value as TScheduleWithDeliveryItem[];
          const inputValue: ScheduleWithDeliveryFormData[] = formValue.map(scheduleItem => ({
            id: scheduleItem.id,
            repetitionDayOfWeek: getArrayOfDays(scheduleItem.repetition_day_of_week),
            startHour: scheduleItem.start_hour,
            endHour: scheduleItem.end_hour,
            assignment_hour: scheduleItem.assignment_hour,
            deliveryHour: scheduleItem.delivery_hour,
            estimation_time: scheduleItem.estimation_time,
          }));

          return (
            <>
              <DailySchedulesWithDelivery
                value={{ schedules: inputValue }}
                isOptionalEndHour={isOptionalEndHour}
                onChange={data => {
                  const schedule: ScheduleItemType[] = data.schedules.map(scheduleData => ({
                    id: scheduleData.id,
                    start_hour: scheduleData.startHour,
                    end_hour: scheduleData.endHour,
                    repetition_day_of_week: scheduleData.repetitionDayOfWeek.join(','),
                    assignment_hour: scheduleData.assignment_hour,
                    delivery_hour: scheduleData.deliveryHour,
                    estimation_time: scheduleData.estimation_time,
                  }));

                  onChange(schedule);
                }}
              />

              {error && (
                <FormHint color={FontColorEnum.Error20} type="error">
                  {error.message}
                </FormHint>
              )}
            </>
          );
        }}
      />
    </FormField>
  );
}
